"use client";

import { useState, type CSSProperties, type ReactNode } from "react";
import MediaImage from "@/components/ui/MediaImage";
import Carousel from "@/components/carousel/Carousel";
import {
  CAROUSEL_BASE,
  type CarouselSettings,
} from "@/components/carousel/carousel-settings";
import CaseMediaViewer from "@/components/ui/CaseMediaViewer";
import type { CollageLayout, PhotoDisplay } from "./model";
import type { ListingViewPhoto } from "./view";

/** The photos in a card: no keys of their own, one photo at a time with a counter in the corner. */
const LISTING_PHOTOS: CarouselSettings = {
  ...CAROUSEL_BASE,
  effect: "slide",
  loop: true,
  arrowStyle: "filled",
  arrowPosition: "sides",
  showDots: true,
  dotStyle: "counter",
  dotPosition: "corner",
  controlSize: "md",
};

/** How many small tiles a collage draws beside or under its big photo. */
export function collageTiles(layout: CollageLayout, count: number) {
  const shape = layout === "auto" ? (count >= 4 ? "top3" : "top2") : layout;
  const small = shape === "top3" ? 3 : 2;
  return { shape, small: Math.min(small, Math.max(0, count - 1)) };
}

/** The listing's photos in a card or its details: one photo, a collage (1 big and 2 or 3 small, »+N« on the last), a
 *  carousel or Duo's two. Each photo opens the shared lightbox, with swipe; a blurred photo never does. Medier's
 *  description stands in for the alt text only where the view allows it (Fuld visning); elsewhere `legacyAlt` keeps
 *  the generated text, whatever the page's description map holds. */
export default function ListingMedia({
  photos,
  display,
  collageLayout = "auto",
  lightbox,
  title,
  className = "",
  style,
  cardPhoto = false,
}: {
  photos: ListingViewPhoto[];
  display: PhotoDisplay;
  collageLayout?: CollageLayout;
  lightbox: boolean;
  title: string;
  className?: string;
  style?: CSSProperties;
  /** In a look-system card: the photos zoom by the section's motion level (src/styles/motion.css). */
  cardPhoto?: boolean;
}) {
  const [viewing, setViewing] = useState<number | null>(null);
  const sharp = photos.filter((photo) => !photo.blurred);
  const count = photos.length;
  const opens = (photo: ListingViewPhoto) => lightbox && !photo.blurred;
  const open = (photo: ListingViewPhoto) => setViewing(sharp.indexOf(photo));

  const image = (photo: ListingViewPhoto, eager = false) => (
    <MediaImage
      className="pf-ml-photo"
      data-re-card-photo={cardPhoto ? "" : undefined}
      src={photo.url}
      {...(photo.descriptions
        ? { fallbackAlt: photo.alt }
        : { alt: photo.alt, legacyAlt: photo.alt })}
      loading={eager ? undefined : "lazy"}
      style={photo.position ? { objectPosition: photo.position } : undefined}
    />
  );
  const tile = (
    photo: ListingViewPhoto,
    index: number,
    extra?: { more: number },
  ) => {
    const more = extra?.more ?? 0;
    const content = (
      <>
        {image(photo, index === 0)}
        {more > 0 && (
          <span className="pf-ml-more" aria-hidden>
            +{more}
          </span>
        )}
      </>
    );
    return opens(photo) ? (
      <button
        key={index}
        type="button"
        className="pf-ml-photo-button"
        aria-label={
          more > 0
            ? `Vis alle ${count} billeder`
            : `Åbn billede ${index + 1} af ${count}: ${title}`
        }
        onClick={() => open(photo)}
      >
        {content}
      </button>
    ) : (
      <span key={index} className="pf-ml-photo-frame">
        {content}
      </span>
    );
  };

  let body: ReactNode;
  if (display === "collage" && count > 1) {
    const { shape, small } = collageTiles(collageLayout, count);
    const shown = photos.slice(0, 1 + small);
    const more = count - shown.length;
    body = (
      <div className="pf-ml-tiles" data-shape={shape} data-small={small}>
        {shown.map((photo, index) =>
          tile(photo, index, index === shown.length - 1 ? { more } : undefined),
        )}
      </div>
    );
  } else if (display === "carousel" && count > 1) {
    body = (
      <Carousel
        settings={LISTING_PHOTOS}
        label={`Billeder af ${title}`}
        noun={{ one: "billede", many: "billeder" }}
        revealArrows="hover"
        className="pf-ml-photo-carousel"
      >
        {photos.map((photo, index) => tile(photo, index))}
      </Carousel>
    );
  } else if (display === "duo") {
    body = (
      <div className="pf-ml-duo-photos" data-count={Math.min(2, count)}>
        {photos.slice(0, 2).map((photo, index) => tile(photo, index))}
      </div>
    );
  } else body = count ? tile(photos[0], 0) : null;

  return (
    <div
      className={`pf-ml-media-photos ${className}`.trim()}
      data-display={display}
      style={style}
    >
      {body}
      {viewing !== null && viewing >= 0 && (
        <CaseMediaViewer
          caseData={{
            images: [],
            imageUrl: "",
            videoUrls: [],
            advertisementAddressOrDisplayName: title,
            shortAddress: title,
          }}
          variant="slideshow"
          view="photo"
          initialIndex={viewing}
          swipe
          media={sharp.map((photo, index) => ({
            type: "image",
            image: {
              mdUrl: photo.url,
              lgUrl: photo.url,
              xlUrl: photo.url,
              namespace: "photo",
              pixelHeight: 0,
              pixelWidth: 0,
              description: photo.alt,
              priority: index,
            },
          }))}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
